import { Injectable, computed, signal } from '@angular/core';
import { RenderFlowContext } from '../models/render-flow-context.model';
import { RenderProgressSnapshot } from '../models/render-progress.model';

export interface RenderSessionState {
  context: RenderFlowContext | null;
  status: 'idle' | 'preparing' | 'rendering' | 'uploading' | 'saving' | 'complete' | 'failed';
  progress: RenderProgressSnapshot | null;
  error: string | null;
  /** True while a browser render is active AND the tab is hidden.
   *  UI should show "Keep this tab open and in front while rendering."
   *  Cleared when the tab comes back to the foreground or the render ends. */
  foregroundWarning: boolean;
}

const initialState: RenderSessionState = {
  context: null,
  status: 'idle',
  progress: null,
  error: null,
  foregroundWarning: false
};

@Injectable({ providedIn: 'root' })
export class RenderSessionStore {
  private readonly stateSignal = signal<RenderSessionState>(initialState);

  readonly state = computed(() => this.stateSignal());
  readonly context = computed(() => this.stateSignal().context);
  readonly progress = computed(() => this.stateSignal().progress);
  readonly isRendering = computed(() => ['preparing', 'rendering', 'uploading', 'saving'].includes(this.stateSignal().status));
  readonly foregroundWarning = computed(() => this.stateSignal().foregroundWarning);

  start(context: RenderFlowContext): void {
    this.stateSignal.set({ context, status: 'preparing', progress: null, error: null, foregroundWarning: false });
  }

  update(patch: Partial<RenderSessionState>): void {
    this.stateSignal.update(current => ({ ...current, ...patch }));
  }

  complete(): void {
    this.update({ status: 'complete', error: null });
  }

  fail(error: string): void {
    this.update({ status: 'failed', error });
  }

  reset(): void {
    this.stateSignal.set(initialState);
  }
}
